import React, {useRef} from 'react';
import {motion} from 'framer-motion';
import RACOAPTERETOField from './RACOAPTERETOField';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import {racoaptereto} from '../data/racoaptereto';

const {FiZap, FiTrash2} = FiIcons;

const RACOAPTERETOForm = ({promptData, setPromptData, setGeneratedPrompt}) => {
  // Crear referencias para cada campo del formulario y para la sección de recuadros
  const fieldRefs = useRef({});
  const recuadrosRef = useRef(null);

  const updateField = (key, value) => {
    setPromptData(prev => ({...prev, [key]: value}));
  };

  const clearAll = () => {
    setPromptData({});
    setGeneratedPrompt('');
  };

  // Función para desplazarse al campo correspondiente
  const scrollToField = (key) => {
    if (fieldRefs.current[key]) {
      fieldRefs.current[key].scrollIntoView({behavior: 'smooth', block: 'center'});
      
      // Destacar brevemente el campo al que se ha desplazado
      const fieldElement = fieldRefs.current[key];
      fieldElement.classList.add('highlight-field');
      
      // Eliminar la clase después de la animación
      setTimeout(() => {
        fieldElement.classList.remove('highlight-field');
      }, 1500);
    }
  };

  // Función para desplazarse hacia los recuadros superiores
  const scrollToRecuadros = () => {
    if (recuadrosRef.current) {
      recuadrosRef.current.scrollIntoView({behavior: 'smooth', block: 'start'});
      
      // Destacar brevemente los recuadros
      recuadrosRef.current.classList.add('highlight-recuadros');
      
      // Eliminar la clase después de la animación
      setTimeout(() => {
        recuadrosRef.current.classList.remove('highlight-recuadros');
      }, 1500);
    }
  };

  // Función para aplicar reglas gramaticales según la RAE
  const applyGrammarRules = (text) => {
    if (!text) return '';
    
    // Normalizar espacios
    let processed = text.trim().replace(/\s+/g, ' ');
    
    // Asegurar que comienza con mayúscula
    processed = processed.charAt(0).toUpperCase() + processed.slice(1);
    
    return processed;
  };

  // Función para determinar si se necesita una coma antes de un gerundio
  const needsCommaBeforeGerund = (text) => {
    // Buscar gerundios (-ando, -endo, -iendo) no precedidos por coma
    const gerundPattern = /([^,;.:])\s+([a-záéíóúüñ]+(?:ando|iendo|endo)\b)/gi;
    return gerundPattern.test(text);
  };

  // Función para determinar si se necesita una coma antes de "para" cuando expresa finalidad
  const needsCommaBeforePara = (text) => {
    // Buscar "para" seguido de infinitivo o "que" (finalidad) no precedido por coma
    const paraPattern = /([^,;.:])\s+(para\s+(?:[a-záéíóúüñ]+(?:ar|er|ir)|que))/gi;
    return paraPattern.test(text);
  };

  // Función para añadir comas antes de gerundios
  const addCommasBeforeGerunds = (text) => {
    // Patrón para detectar gerundios no precedidos por coma
    const gerundPattern = /([^,;.:])\s+([a-záéíóúüñ]+(?:ando|iendo|endo)\b)/gi;
    
    // Añadir coma antes del gerundio
    return text.replace(gerundPattern, '$1, $2');
  };

  // Función para añadir comas antes de "para" cuando introduce finalidad
  const addCommasBeforePara = (text) => {
    // Patrón para detectar "para" no precedido por coma cuando introduce finalidad
    const paraPattern = /([^,;.:])\s+(para\s+(?:[a-záéíóúüñ]+(?:ar|er|ir)|que))/gi;
    
    // Añadir coma antes de "para"
    return text.replace(paraPattern, '$1, $2');
  };

  // Función para determinar el separador apropiado entre elementos
  const getAppropiateSeparator = (index, totalItems) => {
    const isLastItem = index === totalItems - 1;
    
    // Para el último elemento, siempre terminar con punto
    if (isLastItem) return '.';
    
    // Para elementos intermedios, usar punto por defecto según la RAE
    return '. ';
  };

  // Función para aplicar puntuación correcta según RAE
  const applyCorrectPunctuation = (text) => {
    if (!text) return '';
    
    let punctuated = text;
    
    // 1. Añadir comas antes de gerundios
    punctuated = addCommasBeforeGerunds(punctuated);
    
    // 2. Añadir comas antes de "para" cuando introduce finalidad
    punctuated = addCommasBeforePara(punctuated);
    
    // 3. Eliminar puntuación al final si existe
    punctuated = punctuated.replace(/[.,;:!?]$/, '');
    
    return punctuated;
  };

  const generatePrompt = () => {
    const filledFields = Object.entries(promptData).filter(([_, value]) => value?.trim());
    
    if (filledFields.length === 0) {
      alert('Por favor, completa al menos un campo antes de generar el prompt.');
      return;
    }
    
    let prompt = '';
    
    filledFields.forEach(([key, value], index) => {
      const fieldData = racoaptereto.find(item => item.key === key);
      
      if (fieldData && value.trim()) {
        // Formatear el valor: asegurar mayúscula inicial y aplicar reglas RAE
        let formattedValue = applyGrammarRules(value);
        
        // Aplicar reglas de puntuación correctas
        formattedValue = applyCorrectPunctuation(formattedValue);
        
        // Determinar el separador apropiado según contexto
        const separator = getAppropiateSeparator(index, filledFields.length);
        
        // Evitar duplicar puntuación
        if (formattedValue.endsWith('.') || formattedValue.endsWith(',') || formattedValue.endsWith(';') || formattedValue.endsWith(':')) {
          // Si ya tiene puntuación al final, la eliminamos para usar nuestro separador
          formattedValue = formattedValue.replace(/[.,;:]$/, '');
        }
        
        prompt += formattedValue + separator;
      }
    });
    
    setGeneratedPrompt(prompt.trim());
  };

  // Dividir los elementos RACOAPTERETO en grupos para una mejor visualización en móviles
  const renderResponsiveElements = () => {
    return (
      <div className="space-y-3 max-w-full overflow-hidden" ref={recuadrosRef}>
        {/* Vista para móviles: mostrar elementos en grupos de 4 */}
        <div className="md:hidden">
          {/* Primera fila: R-A-C-O */}
          <div className="grid grid-cols-4 gap-1 mb-2">
            {racoaptereto.slice(0, 4).map((item) => (
              <motion.div 
                key={item.key}
                initial={{opacity: 0, scale: 0.8}}
                animate={{opacity: 1, scale: 1}}
                transition={{delay: 0.1}}
                className={`${item.key === 'rol' || item.key === 'contexto' ? 'bg-[#2B5596]' : 'bg-[#F54CC8]'} text-white rounded-lg p-2 text-center cursor-pointer hover:shadow-lg hover:scale-105 transition-all`}
                onClick={() => scrollToField(item.key)}
                aria-label={`Ir a ${item.title}`}
              >
                <div className="font-bold text-lg">{item.letter}</div>
                <div className="text-xs opacity-90">{item.title}</div>
              </motion.div>
            ))}
          </div>
          
          {/* Segunda fila: A-P-T-E */}
          <div className="grid grid-cols-4 gap-1 mb-2">
            {racoaptereto.slice(4, 8).map((item) => (
              <motion.div 
                key={item.key}
                initial={{opacity: 0, scale: 0.8}}
                animate={{opacity: 1, scale: 1}}
                transition={{delay: 0.2}}
                className={`${item.key === 'audiencia' || item.key === 'tarea' ? 'bg-[#2B5596]' : 'bg-[#F54CC8]'} text-white rounded-lg p-2 text-center cursor-pointer hover:shadow-lg hover:scale-105 transition-all`}
                onClick={() => scrollToField(item.key)}
                aria-label={`Ir a ${item.title}`}
              >
                <div className="font-bold text-lg">{item.letter}</div>
                <div className="text-xs opacity-90">{item.title}</div>
              </motion.div>
            ))}
          </div>
          
          {/* Tercera fila: R-E-T-O */}
          <div className="grid grid-cols-4 gap-1">
            {racoaptereto.slice(8, 12).map((item) => (
              <motion.div 
                key={item.key}
                initial={{opacity: 0, scale: 0.8}}
                animate={{opacity: 1, scale: 1}}
                transition={{delay: 0.3}}
                className={`${item.key === 'resultado' || item.key === 'tono' ? 'bg-[#2B5596]' : 'bg-[#F54CC8]'} text-white rounded-lg p-2 text-center cursor-pointer hover:shadow-lg hover:scale-105 transition-all`}
                onClick={() => scrollToField(item.key)}
                aria-label={`Ir a ${item.title}`}
              >
                <div className="font-bold text-lg">{item.letter}</div>
                <div className="text-xs opacity-90">{item.title}</div>
              </motion.div>
            ))}
          </div>
        </div>
        
        {/* Vista para tablets: mostrar elementos en dos filas */}
        <div className="hidden md:block lg:hidden">
          {/* Primera fila: R-A-C-O-A-P */}
          <div className="grid grid-cols-6 gap-2 mb-2">
            {racoaptereto.slice(0, 6).map((item) => (
              <motion.div 
                key={item.key}
                initial={{opacity: 0, scale: 0.8}}
                animate={{opacity: 1, scale: 1}}
                transition={{delay: 0.1}}
                className={`${item.key === 'rol' || item.key === 'contexto' || item.key === 'audiencia' ? 'bg-[#2B5596]' : 'bg-[#F54CC8]'} text-white rounded-lg p-2 text-center cursor-pointer hover:shadow-lg hover:scale-105 transition-all`}
                onClick={() => scrollToField(item.key)}
                aria-label={`Ir a ${item.title}`}
              >
                <div className="font-bold text-lg">{item.letter}</div>
                <div className="text-xs opacity-90">{item.title}</div>
              </motion.div>
            ))}
          </div>
          
          {/* Segunda fila: T-E-R-E-T-O */}
          <div className="grid grid-cols-6 gap-2">
            {racoaptereto.slice(6, 12).map((item) => (
              <motion.div 
                key={item.key}
                initial={{opacity: 0, scale: 0.8}}
                animate={{opacity: 1, scale: 1}}
                transition={{delay: 0.2}}
                className={`${item.key === 'tarea' || item.key === 'resultado' || item.key === 'tono' ? 'bg-[#2B5596]' : 'bg-[#F54CC8]'} text-white rounded-lg p-2 text-center cursor-pointer hover:shadow-lg hover:scale-105 transition-all`}
                onClick={() => scrollToField(item.key)}
                aria-label={`Ir a ${item.title}`}
              >
                <div className="font-bold text-lg">{item.letter}</div>
                <div className="text-xs opacity-90">{item.title}</div>
              </motion.div>
            ))}
          </div>
        </div>
        
        {/* Vista para escritorio: mostrar todos en una fila */}
        <div className="hidden lg:block overflow-x-auto">
          <div className="grid grid-cols-12 gap-2">
            {racoaptereto.map((item, index) => (
              <motion.div 
                key={item.key}
                initial={{opacity: 0, scale: 0.8}}
                animate={{opacity: 1, scale: 1}}
                transition={{delay: index * 0.05}}
                className={`${['rol', 'contexto', 'audiencia', 'tarea', 'resultado', 'tono'].includes(item.key) ? 'bg-[#2B5596]' : 'bg-[#F54CC8]'} text-white rounded-lg p-3 text-center cursor-pointer hover:shadow-lg hover:scale-105 transition-all`}
                onClick={() => scrollToField(item.key)}
                aria-label={`Ir a ${item.title}`}
              >
                <div className="font-bold text-lg">{item.letter}</div>
                <div className="text-xs opacity-90">{item.title}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <motion.div 
      initial={{opacity: 0, y: 20}}
      animate={{opacity: 1, y: 0}}
      transition={{duration: 0.6, delay: 0.2}}
      className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-4 sm:p-8 w-full overflow-hidden"
    >
      <div className="mb-8">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-4">
          Enfoque <span className="italic">InnovaPrompt</span>
        </h2>
        
        {/* Implementación responsiva de los elementos */}
        {renderResponsiveElements()}
      </div>
      
      <div className="space-y-6 w-full overflow-hidden">
        {racoaptereto.map((item, index) => (
          <motion.div 
            key={item.key}
            initial={{opacity: 0, x: -20}}
            animate={{opacity: 1, x: 0}}
            transition={{delay: index * 0.1}}
            ref={el => fieldRefs.current[item.key] = el}
            className="field-container transition-all duration-300 w-full"
          >
            <RACOAPTERETOField 
              data={item}
              value={promptData[item.key] || ''}
              onChange={(value) => updateField(item.key, value)}
              onNavigateToTop={scrollToRecuadros}
            />
          </motion.div>
        ))}
      </div>
      
      <div className="flex flex-col sm:flex-row gap-4 mt-8">
        <motion.button
          onClick={generatePrompt}
          className="flex-1 bg-[#2B5596] text-white px-6 py-3 rounded-lg font-semibold flex items-center justify-center space-x-2 hover:shadow-lg transition-all"
          whileHover={{scale: 1.02}}
          whileTap={{scale: 0.98}}
        >
          <SafeIcon icon={FiZap} className="w-5 h-5" />
          <span>Generar <span className="italic">InnovaPrompt</span></span>
        </motion.button>
        
        <motion.button
          onClick={clearAll}
          className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-6 py-3 rounded-lg font-semibold flex items-center justify-center space-x-2 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          whileHover={{scale: 1.02}}
          whileTap={{scale: 0.98}}
        >
          <SafeIcon icon={FiTrash2} className="w-5 h-5" />
          <span>Limpiar Todo</span>
        </motion.button>
      </div>
    </motion.div>
  );
};

export default RACOAPTERETOForm;